import React, { useEffect, Fragment, useMemo, useState } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import moment from 'moment';
import { I } from '../../../common/v5/config';
import { CHANNEL_ICONS_MAP, OBJ_ID_NAME } from '../../../common/v5/constants';
import {
	ListHeaderWrapper,
	ListContentWrapper,
	TableHeader
} from '../../../reactcomponents/Admin';
import { customPageTotal } from "../../../reactcomponents/Table";
import { TextInputRow } from '../../../reactcomponents/Form';
import { NoFlipMultiSelect } from '../../../reactcomponents/Dropdown';
import SquareCheckbox from '../../../reactcomponents/SquareCheckbox';
import { Calender } from '../../../reactcomponents/DatePicker';
import {
	centionGreen
	, centionRed
	, centionYellow
 } from '../../../styles/_variables'

interface AnswerLog {
	id: number;
	displayId: string;
	subject: string;
	question: string;
	answer: string;
	date: string;
	closedDate: string;
	channel: number;
	metaData: any;
}

interface AnswerLogsProps {
	list?: AnswerLog[];
	agentList?: any[];
	channels?: any[];
	areaList?: any[];
	isLoading?: boolean;
	onLoad?: () => void;
	onFilterByListby?: (params: any) => void;
	onRateAnswer?: (params: any) => void;
}

const TableOptions = {
	paginationSize: 3,
	pageStartIndex: 1,
	hideSizePerPage: true,
	hidePageListOnlyOnePage: true,
	firstPageText: '<<',
	prePageText: '<',
	nextPageText: '>',
	lastPageText: '>>',
	showTotal: true,
	paginationTotalRenderer: customPageTotal,
	disablePageTitle: true,
	sizePerPageList: [10]
};

// Helper function to format text content
const formatContent = (content: string, maxLength: number = 200) => {
	if (!content) return '';

	// Check if content contains HTML tags
	const hasHTML = /<[^>]*>/g.test(content);

	if (hasHTML) {
		// Strip HTML tags for display
		const strippedContent = content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
		return strippedContent.length > maxLength
			? strippedContent.substring(0, maxLength) + '...'
			: strippedContent;
	}

	// For plain text, preserve line breaks and format nicely
	return content.length > maxLength
		? content.substring(0, maxLength) + '...'
		: content;
};

// CSV Export functionality
const exportToCSV = (data: AnswerLog[], filename: string = 'answer_logs.csv') => {
	if (!data || data.length === 0) {
		alert(I('No data to export'));
		return;
	}
	// Define CSV headers
	const headers = ['ID', 'Subject', 'Question', 'Answer', 'Date', 'Closed Date', 'Channel', 'Rating', 'Rating Comment', 'Rated By'];
	
	// Helper function to clean text for CSV (remove HTML, escape quotes, preserve line breaks)
	const cleanForCSV = (text: string | number | undefined | null): string => {
		if (text === null || text === undefined) return '""';
		
		const str = String(text);
		if (!str || str.trim() === '') return '""';
		
		// Strip HTML tags and decode HTML entities
		let cleaned = str.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
		// Keep line breaks but escape them properly for CSV
		cleaned = cleaned.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
		// Escape double quotes
		cleaned = cleaned.replace(/"/g, '""');
		// Trim whitespace
		cleaned = cleaned.trim();
		return `"${cleaned}"`;
	};

	// Build CSV rows
	const csvRows = [
		headers.join(','),
		...data.map(row => {
			const channelName = row.channel || 'Unknown';

			const feedbackRating = row.metaData?.feedback?.rating;
			const feedbackComment = row.metaData?.feedback?.comment;
			const ratedBy = row.metaData?.feedback?.user;

			let ratingText = '';
			if (feedbackRating === 1) {
				ratingText = 'Helpful';
			} else if (feedbackRating === 2) {
				ratingText = 'Not Helpful';
			}

			return [
				cleanForCSV(row.displayId || row.id || ''),
				cleanForCSV(row.subject || ''),
				cleanForCSV(row.question || ''),
				cleanForCSV(row.answer || ''),
				cleanForCSV(row.date || ''),
				cleanForCSV(row.closedDate || ''),
				cleanForCSV(channelName),
				cleanForCSV(ratingText),
				cleanForCSV(feedbackComment || ''),
				cleanForCSV(ratedBy || '')
			].join(',');
		})
	];

	// Create CSV content with UTF-8 BOM for proper Excel compatibility
	const BOM = '\uFEFF';
	const csvContent = BOM + csvRows.join('\n');

	// Create blob and download
	const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
	const link = document.createElement('a');
	const url = URL.createObjectURL(blob);

	link.setAttribute('href', url);
	link.setAttribute('download', filename);
	link.style.visibility = 'hidden';
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};

const channelFormatter = (cell: number, row: AnswerLog) => {
	const iconClass = CHANNEL_ICONS_MAP[cell] ? CHANNEL_ICONS_MAP[cell] : 'icon-v5-email';
	return <i className={iconClass}></i>;
};

const questionFormatter = (cell: string, row: AnswerLog) => {
	const cleanText = cell ? cell.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() : '';

	return (
		<div
			style={{
				fontSize: '13px',
				lineHeight: '1.4',
				color: '#333',
				wordWrap: 'break-word',
				whiteSpace: 'pre-wrap',
				maxHeight: '150px',
				overflow: 'auto'
			}}
			title={cleanText}
		>
			{formatContent(cell)}
		</div>
	);
};

const answerFormatter = (cell: string, row: AnswerLog) => {
	const cleanText = cell ? cell.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() : '';

	return (
		<div
			style={{
				fontSize: '13px',
				lineHeight: '1.4',
				color: '#333',
				wordWrap: 'break-word',
				whiteSpace: 'pre-wrap',
				maxHeight: '150px',
				overflow: 'auto'
			}}
			title={cleanText}
		>
			{formatContent(cell)}
		</div>
	);
};

const handleRating = (answerId: number, rating: 'helpful' | 'not_helpful', answerText: string, comment: string) => {
	console.log('Answer Rating:', {
		answerId,
		rating: rating === 'helpful' ? 'Helpful' : 'Not Helpful',
		answerPreview: answerText.substring(0, 50) + (answerText.length > 50 ? '...' : ''),
		comment: comment || '(No comment)'
	});
};

const LoadingSpinner: React.FC = () => {
	return (
		<div style={{
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			padding: '60px 20px',
			flexDirection: 'column',
			gap: '15px'
		}}>
			<div
				style={{
					width: '50px',
					height: '50px',
					border: '4px solid #f3f3f3',
					borderTop: '4px solid #3498db',
					borderRadius: '50%',
					animation: 'spin 1s linear infinite'
				}}
			></div>
			<style>{`
				@keyframes spin {
					0% { transform: rotate(0deg); }
					100% { transform: rotate(360deg); }
				}
			`}</style>
			<div style={{ color: '#666', fontSize: '14px' }}>{I('Loading...')}</div>
		</div>
	);
};

const AnswerLogs: React.FC<AnswerLogsProps> = ({ isLoading, list, agentList, channels, areaList, onLoad, onFilterByListby, onRateAnswer }) => {

	interface FilterState {
		keyword: string;
		channel: number | string;
		channelIds?: string;
		agentId: number | string;
		agentIds?: string;
		areaId: number | string;
		areaIds?: string;
		dateRange: number | string;
		fromDate: string;
		toDate: string;
		closedOnly: boolean;
	}

	interface RatingModalState {
		isOpen: boolean;
		answerId: number | null;
		rating: 'helpful' | 'not_helpful' | null;
		answerText: string;
	}

	const UNSELECT = -1;

	const [isSearching, setIsSearching] = useState<boolean>(false);
	const [hasSearched, setHasSearched] = useState<boolean>(false);

	useEffect(() => {
		if (onLoad) {
			onLoad();
		}
	}, []);

	// Reset searching state when new data arrives
	useEffect(() => {
		if (list !== undefined && isSearching) {
			setIsSearching(false);
		}
	}, [list]);

	const data = isSearching ? [] : (list || []);

	const [filters, setFilters] = useState<FilterState>({
		keyword: '',
		channel: UNSELECT,
		agentId: UNSELECT,
		areaId: UNSELECT,
		dateRange: UNSELECT,
		fromDate: '',
		toDate: '',
		closedOnly: false
	});

	const [showArea, setShowArea] = useState<boolean>(false);
	const [showAgent, setShowAgent] = useState<boolean>(false);
	const [showChannel, setShowChannel] = useState<boolean>(false);

	const [ratingModal, setRatingModal] = useState<RatingModalState>({
		isOpen: false,
		answerId: null,
		rating: null,
		answerText: ''
	});

	const [ratingComment, setRatingComment] = useState<string>('');

	const channelOptions = useMemo(() => {
		if (!channels) return [] as any[];

		if (Array.isArray(channels)) {
			return (channels as any[]).map((item: any, idx: number) => {
				if (typeof item === 'string') {
					return { id: idx, name: item };
				}
				if (item && typeof item === 'object') {
					const id = item.id ?? item.value ?? idx;
					const name = item.name ?? item.label ?? String(item);
					return { id, name };
				}
				return item;
			});
		}

		if (typeof channels === 'object') {
			return Object.entries(channels as Record<string, any>).map(([key, value]) => {
				if (typeof value === 'string') {
					const numericKey = Number(key);
					const id = isNaN(numericKey) ? key : numericKey;
					return { id, name: value };
				}
				if (value && typeof value === 'object') {
					const numericKey = Number(key);
					const fallbackId = isNaN(numericKey) ? key : numericKey;
					const id = value.id ?? value.value ?? fallbackId;
					const name = value.name ?? value.label ?? String(value);
					return { id, name };
				}
				const numericKey = Number(key);
				const id = isNaN(numericKey) ? key : numericKey;
				return { id, name: String(value) };
			});
		}

		return [] as any[];
	}, [channels]);

	const AnyTextInputRow: any = TextInputRow as any;

	const buildParams = (f: FilterState) => {
		const params: any = {};
		if (f.keyword && f.keyword.trim()) params.keyword = f.keyword.trim();
		if (f.channelIds && f.channelIds.length > 0) params.channel = f.channelIds;
		else if (f.channel !== UNSELECT) params.channel = f.channel;
		if (f.agentIds && f.agentIds.length > 0) params.agents = f.agentIds;
		else if (f.agentId !== UNSELECT) params.agentId = f.agentId;
		if (f.areaIds && f.areaIds.length > 0) params.areas = f.areaIds;
		else if (f.areaId !== UNSELECT) params.areaId = f.areaId;
		if (f.dateRange !== UNSELECT) params.dateRange = f.dateRange;
		if (f.fromDate) params.fromDate = moment(f.fromDate, 'YYYY/MM/DD').unix();
		if (f.toDate) params.toDate = moment(f.toDate, 'YYYY/MM/DD').endOf('day').unix();
		if (f.closedOnly) params.closedOnly = true;
		return params;
	};

	const handleKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const keyword = e.target.value;
		setFilters(prev => {
			const updated = { ...prev, keyword };
			return updated;
		});
	};

	const handleSelectAreaMulti = (val: string) => {
		setFilters(prev => {
			const updated: FilterState = { ...prev, areaIds: val };
			return updated;
		});
	};

	const handleSearch = () => {
		setIsSearching(true);
		setHasSearched(true);
		onFilterByListby?.(buildParams(filters));
	};

	const handleClearFilters = () => {
		setFilters({
			keyword: '',
			channel: UNSELECT,
			agentId: UNSELECT,
			areaId: UNSELECT,
			dateRange: UNSELECT,
			fromDate: '',
			toDate: '',
			closedOnly: false
		});
		setHasSearched(false);
		onFilterByListby?.({});
	};

	const handleExportCSV = () => {
		const timestamp = new Date().toISOString().split('T')[0];
		exportToCSV(data, `answer_logs_${timestamp}.csv`);
	};

	const handleToggleArea = () => setShowArea(!showArea);
	const handleToggleAgent = () => setShowAgent(!showAgent);
	const handleToggleChannel = () => setShowChannel(!showChannel);

	const handleOpenRatingModal = (answerId: number, rating: 'helpful' | 'not_helpful', answerText: string) => {
		setRatingModal({
			isOpen: true,
			answerId,
			rating,
			answerText
		});
		setRatingComment('');
	};

	const handleCloseRatingModal = () => {
		setRatingModal({
			isOpen: false,
			answerId: null,
			rating: null,
			answerText: ''
		});
		setRatingComment('');
	};

	const handleSubmitRating = () => {
		if (ratingModal.answerId && ratingModal.rating) {
			handleRating(ratingModal.answerId, ratingModal.rating, ratingModal.answerText, ratingComment);
			onRateAnswer({
				errandId: ratingModal.answerId,
				rating: ratingModal.rating === 'helpful' ? 1 : 2,
				comment: ratingComment || '(No comment)'
			});
			handleCloseRatingModal();
		}
	};

	const ratingFormatter = (cell: any, row: AnswerLog) => {
		const feedbackRating = row.metaData?.feedback;
		const helpfulRating = feedbackRating?.rating == 1;
		const notHelpfulRating = feedbackRating?.rating == 2;
		const ratingComment = feedbackRating?.comment;
		const ratedBy = feedbackRating?.user;

		return (
			<div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
				<button
					className="btn btn-sm btn-success"
					style={{
						padding: '6px 10px',
						fontSize: '16px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						color: centionGreen,
						backgroundColor: helpfulRating ? centionGreen : 'transparent',
						border: `1px solid ${centionGreen}`
					}}
					disabled={helpfulRating || notHelpfulRating}
					onClick={() => handleOpenRatingModal(row.id, 'helpful', row.answer)}
					title={ helpfulRating ? ratingComment + " by " + ratedBy : "" }
				>
					<i className="icon-thumbs-up" style={{ color: helpfulRating ? 'white' : 'gray' }}></i>
				</button>
				<button
					className="btn btn-sm btn-danger"
					style={{
						padding: '6px 10px',
						fontSize: '16px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						color: centionRed,
						backgroundColor: notHelpfulRating ? centionRed : 'transparent',
						border: `1px solid ${centionRed}`
					}}
					disabled={helpfulRating || notHelpfulRating}
					onClick={() => handleOpenRatingModal(row.id, 'not_helpful', row.answer)}
					title={ notHelpfulRating ? ratingComment + " by " + ratedBy : "" }
				>
					<i className="icon-thumbs-down" style={{ color: notHelpfulRating ? 'white' : 'gray' }}></i>
				</button>
			</div>
		);
	};

	const columns = [
		{
			dataField: 'displayId',
			text: I('ID'),
			sort: true,
			headerStyle: { width: '5%' }
		},
		{
			dataField: 'channel',
			text: '',
			formatter: channelFormatter,
			headerStyle: { width: '3%' }
		},
		{
			dataField: 'subject',
			text: I('Subject'),
			sort: true,
			headerStyle: { width: '12%' }
		},
		{
			dataField: 'question',
			text: I('Question'),
			sort: true,
			formatter: questionFormatter,
			headerStyle: { width: '25%' }
		},
		{
			dataField: 'answer',
			text: I('Answer'),
			sort: true,
			formatter: answerFormatter,
			headerStyle: { width: '25%' }
		},
		{
			dataField: 'date',
			text: I('Date Answered'),
			sort: true,
			headerStyle: { width: '12%' }
		},
		{
			dataField: 'rating',
			text: I('Rate Answer'),
			formatter: ratingFormatter,
			headerStyle: { width: '18%', textAlign: 'center' },
			sort: false
		}
	];

	const defaultSorted = [{
		dataField: 'date',
		order: 'desc' as const
	}];

	return (
		<div style={{ height: '100%', overflow: 'auto', margin: '20px 20px' }}>
			<ListHeaderWrapper style={{ display: 'flex' }}>
				<TableHeader
					title={I("Auto Answer Logs")}
					hideAddIcon={true}
					onClickAdd={() => { }}
					addActive={false}
					children={null}
				/>
			</ListHeaderWrapper>
			{!hasSearched && (
				<div style={{
					margin: '20px 15px',
					padding: '15px 20px',
					backgroundColor: '#e8f4f8',
					border: '1px solid #b8dce8',
					borderRadius: '4px',
					fontSize: '14px',
					color: '#0c5460',
					display: 'flex',
					alignItems: 'center',
					gap: '8px'
				}}>
					<i className="icon-info-sign" style={{ fontSize: '16px' }}></i>
					<span>{I('Select any filter and click Search to view the answer logs list')}</span>
				</div>
			)}
			<div className='admin-filters' style={{
				display: 'grid',
				gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
				gap: '15px',
				padding: '20px',
				backgroundColor: '#f8f9fa',
				borderRadius: '4px',
				marginBottom: '20px'
			}}>
				<div className="admin-filters-item">
					<AnyTextInputRow
						id={'answerlogs-search'}
						name={'keyword'}
						className={'admin-filter-select search-field'}
						placeholder={I('Search for...')}
						value={filters.keyword}
						onChange={handleKeywordChange}
						hide={false}
						label={''}
						style={{padding: '5px'}}
					/>
				</div>
				<div>
					<SquareCheckbox
						id={'filter-closed'}
						data-qa-id={'filter-closed'}
						label={I('Closed')}
						checked={filters.closedOnly}
						onClick={(checked: boolean) => setFilters(prev => ({ ...prev, closedOnly: checked }))}
						className={'medium'}
					/>
				</div>
				<NoFlipMultiSelect
					id={'filter-channel'}
					title={I('Select Channel')}
					className="popup-multi-select admin-filter-select"
					overrideTitle={true}
					data={channelOptions || []}
					idFields={{id: "id", name: "name"}}
					selected={filters.channelIds || ''}
					onSelect={(val: string) => setFilters(prev => ({ ...prev, channelIds: val }))}
					onToggle={handleToggleChannel}
					show={showChannel}
					selectAll={true}
					selectNone={true}
				/>
				<NoFlipMultiSelect
					id={'filter-agent'}
					title={I('Select Agent')}
					className="popup-multi-select admin-filter-select"
					overrideTitle={true}
					data={agentList || []}
					idFields={OBJ_ID_NAME}
					selected={filters.agentIds || ''}
					onSelect={(val: string) => setFilters(prev => ({ ...prev, agentIds: val }))}
					onToggle={handleToggleAgent}
					show={showAgent}
					selectAll={true}
					selectNone={true}
				/>

				<NoFlipMultiSelect
					id={'filter-area'}
					title={I('Select Area')}
					className="popup-multi-select admin-filter-select"
					overrideTitle={true}
					groupSelect={true}
					nested={{ key: 'Areas' }}
					data={areaList}
					idFields={OBJ_ID_NAME}
					selected={filters.areaIds || ''}
					onSelect={handleSelectAreaMulti}
					onToggle={handleToggleArea}
					show={showArea}
					selectAll={true}
					selectNone={true}
				/>
				<div>
					<label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 500 }}>
						{I('Answer Date From')}
					</label>
					<Calender
						selected={filters.fromDate}
						onChange={(date: string) => setFilters(prev => ({ ...prev, fromDate: date }))}
					/>
				</div>

				<div>
					<label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 500 }}>
						{I('Answer Date To')}
					</label>
					<Calender
						selected={filters.toDate}
						onChange={(date: string) => setFilters(prev => ({ ...prev, toDate: date }))}
					/>
				</div>
			</div>

			<div style={{ display: 'flex', gap: '10px', padding: '0 20px 20px', alignItems: 'center' }}>
				<button
					type="button"
					className="btn btn-primary"
					onClick={handleSearch}
				>
					{I('Search')}
				</button>
				<button
					type="button"
					className="btn btn-secondary"
					onClick={handleClearFilters}
				>
					{I('Clear')}
				</button>
				<button
					type="button"
					className="btn btn-success"
					onClick={handleExportCSV}
					disabled={!data || data.length === 0}
					style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
				>
					<i className="icon-download" style={{fontSize: '14px'}}></i>
					{I('Export CSV')}
				</button>
			</div>

			<ListContentWrapper className={"v5-table-style adminFilter adminFilter-answerlogs"}>
				<BootstrapTable
					keyField="id"
					data={data}
					noDataIndication={(isLoading || isSearching) ? <LoadingSpinner /> : I("No data to display")}
					columns={columns}
					bordered={false}
					condensed={false}
					defaultSorted={defaultSorted}
					pagination={paginationFactory(TableOptions)}
					hover
				/>
			</ListContentWrapper>

			{/* Rating Comment Modal */}
			{ratingModal.isOpen && (
				<div
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: 'rgba(0, 0, 0, 0.5)',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						zIndex: 9999
					}}
					onClick={handleCloseRatingModal}
				>
					<div
						style={{
							backgroundColor: 'white',
							borderRadius: '8px',
							padding: '24px',
							minWidth: '400px',
							maxWidth: '600px',
							boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
						}}
						onClick={(e) => e.stopPropagation()}
					>
						<h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', fontWeight: 'bold' }}>
							{ratingModal.rating === 'helpful' ? I('Rate as Helpful') : I('Rate as Not Helpful')}
						</h3>
						<div style={{ marginBottom: '16px' }}>
							<label
								style={{
									display: 'block',
									marginBottom: '8px',
									fontSize: '14px',
									fontWeight: '500',
									color: '#333'
								}}
							>
								{I('Add a comment (optional):')}
							</label>
							<textarea
								value={ratingComment}
								onChange={(e) => setRatingComment(e.target.value)}
								placeholder={I('Enter your comment here...')}
								rows={4}
								style={{
									width: '100%',
									padding: '8px',
									fontSize: '14px',
									border: '1px solid #ccc',
									borderRadius: '4px',
									resize: 'vertical',
									fontFamily: 'inherit'
								}}
							/>
						</div>
						<div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
							<button
								type="button"
								className="btn btn-secondary"
								onClick={handleCloseRatingModal}
								style={{ padding: '8px 16px', backgroundColor: centionYellow, border: `1px solid ${centionYellow}` }}
							>
								{I('Cancel')}
							</button>
							<button
								type="button"
								className={ratingModal.rating === 'helpful' ? 'btn btn-success' : 'btn btn-danger'}
								onClick={handleSubmitRating}
								style={{ padding: '8px 16px', backgroundColor: centionGreen, border: `1px solid ${centionGreen}` }}
							>
								{I('Submit Rating')}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default AnswerLogs;

